// Aviso "nova atualização disponível" — pedido 2026-09-03: "eu quero que
// pare de ficar dando tela branca pra atualizar... quero que apareça uma
// notificação atualizar sistema, notificação bem bonita". Dois gatilhos
// independentes, os dois acabam no mesmo aviso:
//
// 1) Polling proativo: compara o build que ESTE cliente carregou
//    (VITE_BUILD_ID, embutido no bundle na hora do build — ver
//    scripts/write-version.ts) com o build que está rodando AGORA no
//    servidor (public/version.json, reescrito a cada deploy). Detecta
//    ANTES de qualquer erro acontecer.
// 2) Rede de segurança: Vite dispara `vite:preloadError` quando um
//    import() dinâmico falha — cenário clássico de quem deixou a aba
//    aberta, um deploy novo apagou o chunk antigo, e ao navegar pra uma
//    rota lazy o fetch desse .js dá 404. Sem capturar isso a promise
//    rejeitada derrubava a árvore React inteira e virava tela branca.
//    Hoje o roteador recarrega sozinho nesse caso; aqui só mostramos o aviso.
//
// Liga/desliga por empresa via branding.updateNotificationEnabled
// (Administração > Identidade Visual > Sistema) — default true.
import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useAdminBranding } from "@/hooks/queries";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

function myBuildId(): string | null {
  const id = import.meta.env.VITE_BUILD_ID as string | undefined;
  return id && id.trim() ? id.trim() : null;
}

export function UpdateAvailableNotice() {
  const { data: branding } = useAdminBranding();
  const enabled = branding?.updateNotificationEnabled ?? true;
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const mine = myBuildId();
    // Sem VITE_BUILD_ID (dev local, `vite dev` não passa por
    // scripts/write-version.ts) não há o que comparar — só a rede de
    // segurança de vite:preloadError (efeito abaixo) continua ativa.
    if (!mine) return;

    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (!cancelled && data.buildId && data.buildId !== mine) setAvailable(true);
      } catch {
        // rede instável — tenta de novo no próximo ciclo, nunca interrompe o app
      }
    };
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);

  // Ao VOLTAR pro app (sai do segundo plano): se já existe build novo no
  // servidor, recarrega direto, sem aviso. Bug relatado 2026-09-22: cliente
  // do app da Play Store continuava vendo a versão antiga depois do deploy
  // — o Android mantém o WebView vivo em segundo plano, então reabrir o app
  // só "descongela" a página antiga que já estava na memória (o HTML
  // no-store só vale pra quando a página é carregada de novo). É o momento
  // menos intrusivo pra recarregar: a pessoa acabou de voltar, não está no
  // meio de nada. Independe do aviso estar ligado/desligado pelo admin.
  useEffect(() => {
    const mine = myBuildId();
    if (!mine) return;
    let checking = false;
    const onVisible = async () => {
      if (document.visibilityState !== "visible" || checking) return;
      checking = true;
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (data.buildId && data.buildId !== mine) window.location.reload();
      } catch {
        // sem rede agora — o polling acima tenta de novo depois
      } finally {
        checking = false;
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, []);

  // SEMPRE ativo, mesmo com a notificação desligada pelo admin — evitar a
  // tela branca é uma correção de estabilidade, não a notificação em si.
  // Com o aviso desligado, a única alternativa sensata a um crash de
  // verdade é recarregar sozinho (a página já está com uma peça que não
  // existe mais, não tem como continuar do jeito que está); com o aviso
  // ligado, deixamos o usuário escolher a hora.
  useEffect(() => {
    // NÃO chamar preventDefault aqui. Com preventDefault o Vite engole a
    // falha e o import() "resolve" com undefined — o roteador então lê
    // undefined.component e cai no "Erro ao carregar: Cannot read
    // properties of undefined (reading 'component')" (bug 2026-10-12, logo
    // após um deploy). Deixando o erro seguir, o lazyRouteComponent do
    // TanStack reconhece "Failed to fetch dynamically imported module" e
    // recarrega a página sozinho (uma vez), que é o que resolve.
    const onPreloadError = () => {
      if (enabled) {
        setAvailable(true);
      } else {
        window.location.reload();
      }
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    return () => window.removeEventListener("vite:preloadError", onPreloadError);
  }, [enabled]);

  if (!enabled || !available || dismissed) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto flex max-w-sm animate-in fade-in slide-in-from-bottom-4 items-start gap-3 rounded-2xl border border-sky-200 bg-white p-4 shadow-2xl duration-300 dark:border-sky-900 dark:bg-slate-900 sm:inset-x-auto sm:bottom-6 sm:right-6"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sky-100 text-sky-600 dark:bg-sky-950/60 dark:text-sky-400">
        <RefreshCw className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Nova atualização disponível
        </p>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Uma versão mais recente do sistema já está pronta. Atualize para continuar com tudo
          funcionando certinho.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-sky-700"
          >
            Atualizar agora
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Depois
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Fechar aviso"
        className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
